import { useState } from "react";
import { StyleSheet, Text, TextInput, View } from "react-native";
import { TRPCClientError } from "@trpc/client";
import { CHECKOUT_WORDS, type CheckoutGoods } from "@libs/checkout-core";
import { CodeStep as EmailCodeStep } from "@components/EmailCode/CodeStep";
import type { CodeStepLook } from "@components/EmailCode/CodeStep";
import { auth } from "@utils/firebase";
import { captureException } from "@utils/captureException";
import { useAuthSession, useData } from "../../runtime/provider";
import type { AuthSession } from "../../runtime/authSession";
import { useAuthSignInWithCustomToken } from "@utils/firebaseAuth/query/auth";

import { colors } from "./native/colors";

const VALID_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isEmailValid = (email: string) => VALID_EMAIL.test(email.trim());

/**
 * Where the tickets sheet's sign-in is: the address being typed, a code on
 * its way, the code up, the code being checked, or the fan being signed in
 * with it (the checkout carries on once they are).
 */
export type SheetSignInStage =
  | "email"
  | "sending"
  | "code"
  | "verifying"
  | "signing";

/**
 * app-web's tickets sheet, signed out (LIS-975): the address the tickets go
 * to, proven with a code in the sheet before anything is held, as Link in
 * Bio's contact step does — then the fan is signed in to the app with it,
 * and the checkout carries on as the app's does for a signed-in fan. The
 * code is the app's own email sign-in (identity.emailCode), so a new
 * address is a new app account, as signing up is.
 */
export function useSheetSignIn({
  onSignedIn,
}: {
  /** The fan's account is in: the checkout carries on. */
  onSignedIn: () => void;
}) {
  const session = useData();
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState<SheetSignInStage>("email");
  const [challenge, setChallenge] = useState<{
    id: string;
    sentTo: string;
    resendAt: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const authSession = useAuthSession();
  const signIn = useAuthSignInWithCustomToken(auth, { retry: 0 });

  async function start(address: string) {
    const result = await session.api.identity.emailCode.start.mutate({
      email: address,
    });
    setChallenge({
      id: result.challengeId,
      sentTo: address,
      resendAt: Date.now() + result.resendAfterSeconds * 1000,
    });
  }

  async function send() {
    const address = email.trim();
    if (!isEmailValid(address) || stage !== "email") return;
    setError(null);
    setStage("sending");
    try {
      await start(address);
      setStage("code");
    } catch (reason) {
      setStage("email");
      setError("We couldn't send a code. Check the address and try again.");
      captureException(reason, {
        channel: "error",
        area: "auth",
        operation: "tickets-email-code-start",
      });
    }
  }

  async function resend() {
    if (!challenge) return;
    setError(null);
    try {
      await start(challenge.sentTo);
    } catch (reason) {
      setError("Something went wrong. Please try again.");
      captureException(reason, {
        channel: "error",
        area: "auth",
        operation: "tickets-email-code-start",
      });
      throw reason;
    }
  }

  /** Checks the code, signs the fan in with it and, once their account is
   *  in, lets the checkout carry on. */
  async function verify(code: string) {
    if (!challenge || stage !== "code") return false;
    setError(null);
    setStage("verifying");
    try {
      const { customToken } =
        await session.api.identity.emailCode.verify.mutate({
          challengeId: challenge.id,
          code,
        });
      setStage("signing");
      await signIn.mutateAsync({ token: customToken });
    } catch (reason) {
      const code =
        reason instanceof TRPCClientError ? reason.data?.code : undefined;
      setStage("code");
      setError(
        code === "BAD_REQUEST"
          ? "That code didn't match. Try again or request a new one."
          : code === "CONFLICT"
            ? "This email is linked to another account. Sign in to it instead."
            : "Something went wrong. Please try again.",
      );
      if (code !== "BAD_REQUEST")
        captureException(reason, {
          channel: "error",
          area: "auth",
          operation: "tickets-email-code-verify",
        });
      return;
    }
    try {
      await accountLoaded(authSession);
    } catch (reason) {
      setChallenge(null);
      setStage("email");
      setError("We couldn't sign you in. Please try again.");
      captureException(reason, {
        channel: "error",
        area: "auth",
        operation: "tickets-sign-in",
      });
      return;
    }
    setStage("email");
    onSignedIn();
  }

  return {
    email,
    stage,
    challenge,
    error,
    typeEmail: (next: string) => {
      setEmail(next);
      setError(null);
    },
    send,
    resend,
    verify,
    clearError: () => setError(null),
    /** Back to the address (× on the code step's chip). */
    edit: () => {
      setError(null);
      setChallenge(null);
      setStage("email");
    },
  };
}

export type SheetSignIn = ReturnType<typeof useSheetSignIn>;

/** Signed in to Firebase, the app's account follows (the same entry as every
 *  sign-in): the checkout's tokens need it. */
const accountLoaded = (auth: AuthSession, timeoutMs = 30000) =>
  new Promise<void>((resolve, reject) => {
    const settle = (status: string) => {
      if (status === "logged-in") resolve();
      else if (status === "db-user-error") reject(new Error(status));
      else return false;
      return true;
    };
    if (settle(auth.store.getState().status)) return;
    const timer = setTimeout(() => {
      stop();
      reject(new Error("account_timeout"));
    }, timeoutMs);
    const stop = auth.store.subscribe(({ status }) => {
      if (!settle(status)) return;
      clearTimeout(timer);
      stop();
    });
  });

/** The address field under the tickets; the sheet's button sends the code. */
export function ContactField({ signIn }: { signIn: SheetSignIn }) {
  const busy = signIn.stage !== "email";
  return (
    <View style={styles.contact}>
      <TextInput
        accessibilityLabel="Email"
        autoCapitalize="none"
        autoComplete="email"
        autoCorrect={false}
        editable={!busy}
        inputMode="email"
        keyboardType="email-address"
        onChangeText={signIn.typeEmail}
        onSubmitEditing={() => void signIn.send()}
        placeholder="Your email"
        placeholderTextColor={colors.secondary}
        returnKeyType="send"
        style={[styles.input, busy ? styles.dimmed : null]}
        testID="tickets-email"
        value={signIn.email}
      />
      {signIn.error ? (
        <Text accessibilityRole="alert" style={styles.error}>
          {signIn.error}
        </Text>
      ) : null}
    </View>
  );
}

// The code step on the light tickets sheet.
const CODE_STEP_LOOK: CodeStepLook = {
  text: colors.text,
  secondary: colors.secondary,
  surface: colors.surface,
  border: colors.border,
  filledBorder: colors.border,
  focus: colors.primary,
  action: colors.primary,
  onAction: colors.onPrimary,
  danger: colors.danger,
  success: colors.success,
  successText: colors.successText,
  successFill: "rgba(52, 199, 89, 0.1)",
  chipFill: colors.fill,
  chipBorder: colors.border,
  slotHeight: 52,
  slotRadius: 10,
  slotGap: 8,
  digitSize: 20,
  gap: 12,
};

/**
 * The code, in the sheet: where it went (× to change it), six digits that
 * are checked at the sixth, and when another may be sent. Once checked it
 * names what the purchase carries on to (`goods`), as Link in Bio's does.
 */
export function CodeStep({
  signIn,
  goods,
}: {
  signIn: SheetSignIn;
  goods: CheckoutGoods;
}) {
  return (
    <View style={styles.code}>
      <EmailCodeStep
        email={signIn.challenge?.sentTo ?? signIn.email}
        status={
          signIn.stage === "signing"
            ? "verified"
            : signIn.stage === "verifying"
              ? "checking"
              : "idle"
        }
        verifiedText={CHECKOUT_WORDS[goods].verified}
        error={signIn.error}
        resendAt={signIn.challenge?.resendAt ?? 0}
        onCode={(code) => void signIn.verify(code)}
        onInput={signIn.clearError}
        onResend={signIn.resend}
        onEdit={signIn.edit}
        look={CODE_STEP_LOOK}
        ids="tickets-code"
        events={{
          changeEmail: "SheetContact.ChangeEmail",
          resend: "SheetContact.ResendCode",
          paste: "SheetContact.PasteCode",
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  // With the tickets' own gap, 16 above it, as above their button.
  contact: { gap: 8, marginTop: 4 },
  input: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: 1,
    color: colors.text,
    fontSize: 16,
    height: 48,
    paddingHorizontal: 14,
  },
  dimmed: { opacity: 0.5 },
  error: { color: colors.danger, fontSize: 13 },
  code: { marginTop: 4 },
});
